<template>
  <div>
    <!--Breadcrumb button-->
    <breadcrumb-button hide-settings>
      <template #beforeDropdown>
        <b-button variant="success" class="btn-icon" v-b-tooltip v-bind:title="$t('operations.add')">
          <i class="fa-solid fa-plus"></i>
        </b-button>
        <b-button variant="primary" class="btn-icon mx-1" v-b-tooltip v-bind:title="$t('columns.self')"
                  @click="openColumnsModal">
          <i class="fa-solid fa-bars"></i>
        </b-button>
      </template>
    </breadcrumb-button>
    <!--Filters-->
    <filter-card  v-model="filter" v-bind:fields="filterFields"/>
    <!--Table Card-->
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5>{{ $t('menu.closedTrades') }}</h5>
      </b-card-header>
      <b-card-body>
        <closed-trade-table ref="closedTradeTable" v-bind:filters="filter" v-bind:columns="columns" :has-selection="true"
                        @selected="onClosedTradeSelected" @selection-changed="onSelectionChange" :lazy="true" open-only>
          <template #table-top-actions="data">
            <b-button size="sm" class="mb-1 mr-1 client-action-button" variant="outline-primary"
                      @click="openMassOrder">
              <i class="fa-solid fa-arrows-rotate"></i> &nbsp; &nbsp;
              <span class="description">{{ $t('operations.openMassOrder') }}</span>
            </b-button>
            <b-button size="sm" id="set-representative" class="mr-1 mb-1 d-inline-block client-action-button"
                      variant="outline-success" @click="openMassCloseOrder" v-bind:disabled="!hasOneSymbolSelected"
            >
              <i class="fa-solid fa-times"></i> &nbsp; &nbsp;
              <span class="description">{{ $t('operations.close') }}</span>
            </b-button>
            <b-button size="sm" class="mr-1 mb-1 d-inline-block client-action-button"
                      variant="outline-danger" @click="openBulkDelete" v-bind:disabled="!selectedOrders.length"
            >
              <i class="fa-solid fa-trash-can"></i> &nbsp; &nbsp;
              <span class="description">{{ $t('operations.delete') }}</span>
              <span v-if="selectedOrders.length"> ({{ selectedOrders.length }})</span>
            </b-button>
          </template>
        </closed-trade-table>
      </b-card-body>
    </b-card>
    <!--Modal-->
    <columns-modal ref="columns-modal" column-key="tradesOpen" v-model="columns"
                   v-bind:columns-names="columnsNames" v-bind:columns-default="columnsDefaultNames"
    />
    <trade-order-modal ref="trade-order-modal" @close="refreshTables"/>
    <trade-mass-oder-modal ref="trade-mass-order-modal" @close="refreshTables"/>
    <trade-mass-close-order-modal ref="trade-mass-close-order-modal" @close="refreshTables" @changed="refreshTables"/>
    <trade-bulk-delete-modal ref="bulkDeleteModal" @done="onBulkDeleteDone"/>
  </div>
</template>

<script>

import { mapActions, mapGetters } from 'vuex';
import BreadcrumbButton from '@/components/widget/breadcrumbButton';
import ClosedTradeSearchRequest from '@/model/trade/TradeSearchRequest';
import ColumnsModal from '@/components/modal/columnsModal';
import { getDefaultOpenTradeFilter, getTradeFields } from '@/model/trade/TradeColumns'
import mixinPermissions from '@/components/mixin/mixinPermissions';
import mixinBase from '@/components/mixin/mixinBase';
import FilterCard from '@/components/widget/FilterCard';
import ClosedTradeFilterFields from '@/model/filter/TradeFilterFields';
import ClosedTradeTable from '@/components/tables/ClosedTradeTable';
import TradeOrderModal from '@/components/modal/tradeOrderModal.vue'
import TradeMassOderModal from '@/components/modal/tradeMassOderModal.vue'
import TradeMassCloseOrderModal from '@/components/modal/TradeMassCloseOrderModal.vue'
import TradeBulkDeleteModal from '@/components/modal/TradeBulkDeleteModal.vue'

export default {
  name: 'OpenTradesView',
  components: {
    TradeBulkDeleteModal,
    TradeMassCloseOrderModal,
    TradeMassOderModal,
    TradeOrderModal,
    ClosedTradeTable,
    FilterCard,
    ColumnsModal,
    BreadcrumbButton,
  },
  mixins: [mixinBase, mixinPermissions],
  data() {
    return {
      columnsNames: getTradeFields(),
      columnsDefaultNames: getDefaultOpenTradeFilter(),
      filter: ClosedTradeSearchRequest(),
      filterLoaded: false,
      columns: [],
      filterFields: null,
      selectedOrders: [],
    };
  },
  watch: { },
  async created() {
    this.filterFields = ClosedTradeFilterFields();
  },
  computed: {
    ...mapGetters('data', ['allOrganizations']),
    hasOneSymbolSelected(){
      let selectedOrders = this.selectedOrders || [];
      let symbolSet = new Set();
      selectedOrders.filter(o=>o.isOpen).forEach(order => {
        symbolSet.add(order.symbolId);
      });
      return symbolSet.size === 1;
    }
  },
  methods: {
    ...mapActions('trade', ['getAllTrades']),
    openColumnsModal() {
      this.$refs['columns-modal'].showModal();
    },
    onClosedTradeSelected(item) {
      // console.log("onClosedTradeSelected", selectedId)
      // this.$refs['trade-modal'].showModal(item.order, item.account);
      this.$refs['trade-order-modal'].showModal(null, item);
    },
    onSelectionChange(value){
      this.selectedOrders = value || [];
    },
    openMassOrder() {
      this.$refs['trade-mass-order-modal'].showModal();
    },
    openMassCloseOrder() {
      if(this.hasOneSymbolSelected) {
        this.$refs['trade-mass-close-order-modal'].showModal(this.selectedOrders.filter(o=>o.isOpen).map(o =>({
          order:o.order,
          account: o.account
        })), this.selectedOrders[0].symbolId);
      }
    },
    // Bulk delete: any selection, any mix of symbols/accounts — the modal
    // runs the same per-trade deleteOrder as the single row delete.
    openBulkDelete() {
      if (!this.selectedOrders.length) return;
      this.$refs.bulkDeleteModal.open(this.selectedOrders);
    },
    onBulkDeleteDone() {
      this.selectedOrders = [];
      const table = this.$refs.closedTradeTable;
      if (table && typeof table.clearSelection === 'function') {
        table.clearSelection();
      }
      this.refreshTables();
    },
  }
};
</script>

<style>
.style-chooser .vs__search::placeholder,
.style-chooser .vs__dropdown-toggle,
.style-chooser .vs__dropdown-menu {
  background: #dfe5fb;
  border: none;
  color: #394066;
  text-transform: lowercase;
  font-variant: small-caps;
  height: 10px;
}
</style>
